import { useCallback, useRef } from 'react'
import callable from '../../utils/callable'

/**
 * Custom hook for handling long press and click events
 *
 * @param {Function} longPressCallback
 * @param {Function} onClickCallback
 * @param {Set} isActiveRef - Reference to check if an other long presses are active
 * @param {String} itemId - Unique identifier for the item being pressed
 * @param {Number} ms - Duration in milliseconds to consider as a long press
 * @returns {Object} - Event handlers for long press and click
 */
const useLongPress = (longPressCallback, onClickCallback, isActiveRef, currentId, ms = 625) => {
  const isTouchDevice = () => 'ontouchstart' in window
  const timeoutRef = useRef(null)
  const blockClickRef = useRef(false)
  const isScrollingRef = useRef(false)
  const hasMouseDownRef = useRef(false)

  /**
   * Starts the long press timer
   *
   * @param {Event} e - The event object
   * @returns {void}
   */
  const onStart = useCallback(
    (e) => {
      e.preventDefault?.()
      hasMouseDownRef.current = true
      isActiveRef.current?.add(currentId)

      // very quick consecutive taps
      if (timeoutRef.current || blockClickRef.current) {
        callable(onClickCallback, e)
        clearTimeout(timeoutRef.current)
        blockClickRef.current = true
        isActiveRef.current?.delete(currentId)
        return
      }

      // reset state
      isScrollingRef.current = false
      blockClickRef.current = false

      // set timer for long press
      timeoutRef.current = setTimeout(() => {
        if (isScrollingRef.current) return
        if (isActiveRef.current?.size > 1) return
        if (!isActiveRef.current?.has(currentId)) return

        callable(longPressCallback, e)
        blockClickRef.current = true
        isActiveRef.current?.delete(currentId)
      }, ms)
    },
    [isActiveRef, currentId, longPressCallback, ms, onClickCallback],
  )

  /**
   * Stops the long press timer and calls the click callback
   *
   * @param {Event} e - The event object
   * @returns {void}
   */
  const onStop = useCallback(
    (e) => {
      e.preventDefault?.()
      clearTimeout(timeoutRef.current)
      isActiveRef.current?.delete(currentId)

      // on browser, if there's no mouse down, we don't want to trigger a click
      if (!hasMouseDownRef.current) return
      hasMouseDownRef.current = false

      // if scrolling is detected, reset state
      if (isScrollingRef.current) {
        isScrollingRef.current = false
        blockClickRef.current = false
        timeoutRef.current = null
        return
      }

      if (blockClickRef.current) {
        blockClickRef.current = false
        return
      }
      // regular taps
      callable(onClickCallback, e)
      blockClickRef.current = true
      isActiveRef.current?.delete(currentId)
    },
    [currentId, isActiveRef, onClickCallback],
  )

  const onCancel = useCallback(() => {
    // reset state on cancel
    isScrollingRef.current = true
    clearTimeout(timeoutRef.current)
    isActiveRef.current?.delete(currentId)
  }, [currentId, isActiveRef])

  const onClick = useCallback(
    (e) => {
      e.preventDefault?.()
      if (timeoutRef.current) return

      clearTimeout(timeoutRef.current)
      callable(onClickCallback, e)
      isActiveRef.current?.delete(currentId)
    },
    [isActiveRef, currentId, onClickCallback],
  )

  if (!isTouchDevice()) {
    return {
      // mouse events
      onMouseDown: onStart,
      onMouseUp: onStop,
    }
  }
  return {
    // touch events
    onTouchStart: onStart,
    onTouchMove: onCancel,
    onTouchCancel: onCancel,
    onTouchEnd: onStop,
    onClick,
  }
}

export default useLongPress
